import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  CheckCircle,
  AlertCircle,
  Loader2,
  X,
  Bell,
  Sparkles
} from 'lucide-react';
import newsletterService, { NewsletterSubscription as NewsletterSubscriptionType } from '../services/newsletterService';

interface NewsletterSubscriptionProps {
  variant?: 'default' | 'compact' | 'popup';
  source?: 'blog' | 'homepage' | 'footer' | 'popup';
  title?: string;
  description?: string;
  showNameFields?: boolean;
  className?: string;
  onSuccess?: (email: string) => void;
  onError?: (error: string) => void;
}

const NewsletterSubscription: React.FC<NewsletterSubscriptionProps> = ({
  variant = 'default',
  source = 'blog',
  title = 'Stay Updated',
  description = 'Get the latest insights, tips, and best practices delivered directly to your inbox.',
  showNameFields = false,
  className = '',
  onSuccess,
  onError
}) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPopup, setShowPopup] = useState(false);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    if (!newsletterService.isValidEmail(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const subscriptionData = newsletterService.formatSubscriptionData({
        email: formData.email,
        firstName: showNameFields ? formData.firstName : undefined,
        lastName: showNameFields ? formData.lastName : undefined,
        source
      } as NewsletterSubscriptionType);

      const response = await newsletterService.subscribe(subscriptionData);

      if (response.success) {
        setSuccess(response.message);
        setFormData({ email: '', firstName: '', lastName: '' });
        onSuccess?.(formData.email);
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to subscribe. Please try again.';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSuccess(null);
    setError(null);
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      {showNameFields && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <input
              type="text"
              placeholder="First Name"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
          <div>
            <input
              type="text"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
        </div>
      )}
      
      <div className="flex space-x-3">
        <input
          type="email"
          placeholder="Enter your email address"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          required
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-lg hover:shadow-xl"
        >
          {isSubmitting ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <Mail size={20} />
          )}
          <span>{isSubmitting ? 'Subscribing...' : 'Subscribe'}</span>
        </button>
      </div>
      
      <p className="text-sm text-gray-600">
        No spam, unsubscribe at any time. We respect your privacy.
      </p>
    </form>
  );

  const renderContent = () => (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      <div className="text-center mb-6">
        <div className="flex items-center justify-center space-x-2 mb-4">
          <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
            <Bell size={24} className="text-white" />
          </div>
          <Sparkles size={24} className="text-blue-600" />
        </div>
        
        <h3 className="text-2xl font-bold mb-2">
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {title.split(' ')[0]}
          </span>
          <span className="bg-gradient-to-r from-purple-600 to-green-600 bg-clip-text text-transparent">
            {' ' + title.split(' ').slice(1).join(' ')}
          </span>
        </h3>
        
        <p className="text-gray-600 leading-relaxed">
          {description}
        </p>
      </div>

      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3"
          >
            <CheckCircle size={20} className="text-green-600" />
            <p className="text-green-800">{success}</p>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3"
          >
            <AlertCircle size={20} className="text-red-600" />
            <p className="text-red-800">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {!success && renderForm()}
    </div>
  );

  if (variant === 'popup') {
    return (
      <>
        <button
          onClick={() => setShowPopup(true)}
          className="fixed bottom-6 right-6 p-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-50"
        >
          <Mail size={24} />
        </button>

        <AnimatePresence>
          {showPopup && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
              onClick={handleClosePopup}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="relative max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={handleClosePopup}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={20} />
                </button>
                {renderContent()}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${className}`}>
        <div className="flex items-center space-x-3">
          <Mail size={20} className="text-blue-600" />
          <div className="flex-1">
            <input
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
            />
          </div>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : 'Subscribe'}
          </button>
        </div>
        
        <AnimatePresence>
          {success && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2"
            >
              <CheckCircle size={16} className="text-green-600" />
              <p className="text-green-800 text-sm">{success}</p>
            </motion.div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2"
            >
              <AlertCircle size={16} className="text-red-600" />
              <p className="text-red-800 text-sm">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return renderContent();
};

export default NewsletterSubscription;
